/*
 *  CollapsibleText.jsx
 *
 *
 *
 */
//==============================================================================
// External dep.
//==============================================================================
import styled from "styled-components";
import React, { useState, useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import TextEllipsis from "react-text-ellipsis";
import { TypeAnimation } from "react-type-animation";
import { Flexbox, Footnote } from "@talentbait/shared-components";
import Loader from "../Loader";
//------------------------------------------------------------------------------
// Styled Components
//------------------------------------------------------------------------------
const DEFAULT_TEXT_STYLE = `
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: 0.03em;
  text-align: left;
  font-family: "Source Sans Pro";
`;
const CollapsibleTextWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  ${DEFAULT_TEXT_STYLE}
  & .hashtag {
    color: #2d89ff;
  }

  ${({ isCollapsed }) =>
    !isCollapsed &&
    `
    & .textEllipsis {
      display: block !important;
    }
  `}

  .talentbait-ai-logo {
    flex-wrap: wrap;

    p {
      white-space: nowrap;
      color: ${({ isDark }) => (isDark ? "black" : "gray")};
    }

    img {
      height: 1rem;
      width: auto;
    }
  }

  .talentbait-ai-loader {
    margin: auto;
    flex-direction: ${({ isShort }) => (isShort ? "row-reverse" : "column")};

    p {
      white-space: nowrap;
      color: gray;
    }

    img {
      height: ${({ isShort }) => (isShort ? 1.2 : 1.6)}rem;
      width: auto;
    }
  }

  .actions-container {
    margin-top: auto;
    padding-top: 16px;
    padding-bottom: 4px;

    @media print {
      display: none;
    }
  }
`;
const HiddenEllipsisCalculator = styled.div`
  visibility: hidden;
  height: 0;
`;
const ShowMoreButton = styled.button`
  ${DEFAULT_TEXT_STYLE}
  font-weight: 600;
  text-decoration: none;
  border: none;
  background-color: transparent;
  padding: 0;
  cursor: pointer;
  user-select: none;
  &:hover {
    text-decoration: underline;
  }
`;

const PlaceholderText = styled.span`
  color: grey;
`;

const colorHashTags = (string) => {
  return (string || "").replace(
    /(^|\s)(#[A-Za-z0-9\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF]+)/gi,
    (m, g1, g2) => {
      return g1 + "<span class='hashtag'>" + g2 + "</span>";
    }
  );
};

const CollapsibleText = ({
  text,
  textStyle,
  lines = 6,
  showMoreButton = true,
  isLoading,
  isAutoGenerated,
  isAnimated,
  placeholder,
  onFinishTyping,
  isShort,
  isDark
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [canCollapse, setCanCollapse] = useState(false);
  const [isFinishedAnimating, setIsFinishedAnimating] = useState(false);

  const handleCanCollapse = useCallback((result) => {
    if (result === TextEllipsis.RESULT.TRUNCATED) {
      setCanCollapse(true);
    } else {
      setCanCollapse(false);
    }
  }, []);

  const toggleTextCollapse = useCallback((event) => {
    event.stopPropagation();
    setIsCollapsed((collapse) => !collapse);
  }, []);

  const coloredText = useMemo(() => colorHashTags(text), [text]);
  const showPlaceholder = useMemo(() => placeholder && !text, [text, placeholder]);

  const typedTextSequence = useMemo(() => {
    if (isAnimated && text) {
      const sequence = [];
      let cummuliativeWord = "";
      text?.split(" ").forEach((word) => {
        cummuliativeWord += word + " ";
        sequence.push(cummuliativeWord);
        sequence.push(100);
      });
      sequence.push(() => {
        setIsFinishedAnimating(true);
        onFinishTyping();
      });
      return sequence;
    }
  }, [text, isAnimated, onFinishTyping]);

  return (
    <CollapsibleTextWrapper
      isCollapsed={isCollapsed}
      isShort={isShort}
      style={textStyle}
      isDark={isDark}
    >
      {isLoading ? (
        <Flexbox
          alignItems="center"
          gap={isShort ? 14 : 8}
          row={isShort}
          className="talentbait-ai-loader"
        >
          <Loader size={isShort ? 40 : 80} />
          <img src="/images/talentbait-ai.svg" />
        </Flexbox>
      ) : showPlaceholder ? (
        <PlaceholderText>{placeholder}</PlaceholderText>
      ) : (
        <>
          <HiddenEllipsisCalculator>
            <TextEllipsis lines={lines} onResult={handleCanCollapse}>
              {text}
            </TextEllipsis>
          </HiddenEllipsisCalculator>
          <TextEllipsis lines={lines} tagClass="textEllipsis">
            {isAnimated && typedTextSequence && !isFinishedAnimating ? (
              <TypeAnimation sequence={typedTextSequence} wrapper="div" cursor={true} speed={70} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: coloredText }} />
            )}
          </TextEllipsis>
        </>
      )}
      {((canCollapse && showMoreButton) || (isAutoGenerated && !isLoading && text)) && (
        <Flexbox gap={8} alignItems="flex-start" className="actions-container">
          {canCollapse && showMoreButton && (
            <ShowMoreButton type="button" style={textStyle} onClick={toggleTextCollapse}>
              {isCollapsed ? t("more") : t("less")}
            </ShowMoreButton>
          )}
          {isAutoGenerated && !isLoading && text && (
            <Flexbox
              row
              gap={6}
              alignItems="center"
              justifyContent="flex-end"
              className="talentbait-ai-logo"
            >
              <Trans parent={Footnote}>Content generated by</Trans>{" "}
              <img src="/images/talentbait-ai.svg" />
            </Flexbox>
          )}
        </Flexbox>
      )}
    </CollapsibleTextWrapper>
  );
};

export default CollapsibleText;
